import styled from 'styled-components';
import { colors } from 'styles/colors';

import { DonutTier, isStackedTier } from '../../tier';

export const Card = styled.div<{ $tier: DonutTier }>`
  background-color: ${colors.bg['white-0']};
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: ${(p) => (isStackedTier(p.$tier) ? '480px' : '340px')};
  min-width: 0;
  overflow: hidden;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

export const ChartArea = styled.div<{ $tier: DonutTier }>`
  width: 100%;
  min-width: 0;
  height: ${(p) => (isStackedTier(p.$tier) ? '300px' : '300px')};
  overflow: hidden;
`;

export const Legend = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 8px;
  padding: 0 4px;
`;

export const LegendItem = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  background: transparent;
  border: 0;
  padding: 4px 0;
  cursor: pointer;
  text-align: left;
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  transition: opacity 120ms ease;

  &:hover {
    opacity: ${(p) => (p.$disabled ? 0.6 : 0.85)};
  }
`;

export const LegendSwatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  border-radius: 2px;
  background-color: ${(p) => p.$color};
`;

export const LegendLabel = styled.span<{ $disabled: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text['main-900']};
  text-decoration: ${(p) => (p.$disabled ? 'line-through' : 'none')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const TooltipRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: inherit;
`;

export const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TooltipSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background-color: ${(p) => p.$color};
`;

export const TooltipName = styled.strong`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

export const TooltipValue = styled.div`
  font-size: 12px;
  color: ${colors.text['sub-500']};
`;

export const SkeletonLayout = styled.div<{ $tier: DonutTier }>`
  width: 100%;
  display: flex;
  flex-direction: ${(p) => (isStackedTier(p.$tier) ? 'column' : 'row')};
  align-items: center;
  justify-content: ${(p) => (isStackedTier(p.$tier) ? 'flex-start' : 'space-between')};
  gap: ${(p) => (isStackedTier(p.$tier) ? '24px' : '32px')};
  padding: ${(p) => (isStackedTier(p.$tier) ? '8px 0' : '0 8px')};
`;

export const SkeletonRingWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 220px;
  height: 220px;
`;

export const SkeletonRing = styled.div`
  position: absolute;
  inset: 10px;
  border-radius: 50%;
  background: ${colors.stroke['soft-200']};

  &::after {
    content: '';
    position: absolute;
    inset: 32px;
    border-radius: 50%;
    background: ${colors.bg['white-0']};
  }
`;

export const SkeletonTick = styled.div<{
  $top: string;
  $left?: string;
  $right?: string;
  $flip?: boolean;
}>`
  position: absolute;
  top: ${(p) => p.$top};
  ${(p) => (p.$right ? `right: ${p.$right};` : `left: ${p.$left ?? '0'};`)}
  display: flex;
  flex-direction: ${(p) => (p.$flip ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 4px;
  width: 28px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: ${colors.stroke['soft-200']};
    border-radius: 2px;
    flex-shrink: 0;
  }

  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: ${colors.stroke['soft-200']};
    border-radius: 1px;
  }
`;

export const SkeletonLegendList = styled.div<{ $tier: DonutTier }>`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: ${(p) => (isStackedTier(p.$tier) ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)')};
  column-gap: 24px;
  row-gap: 12px;
  ${(p) => (isStackedTier(p.$tier) ? 'width: 100%; padding: 0 8px;' : 'min-width: 140px;')}
`;

export const SkeletonLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > span {
    flex-shrink: 0;
  }
`;
